import React, { useState } from 'react';
import { X, Eye, AlertCircle, BookOpen, FileText, Brain, CheckSquare, Bell, ClipboardList, Calendar, Clock, Users, Layers, ChevronDown, ChevronRight, Check, Circle, Upload, PenTool, List } from 'lucide-react';

const ITEM_TYPE_CONFIG = {
    task: { icon: FileText, color: 'violet', label: 'Task' },
    quiz: { icon: Brain, color: 'purple', label: 'Quiz' },
    training: { icon: BookOpen, color: 'blue', label: 'Training' },
    checklist: { icon: CheckSquare, color: 'green', label: 'Checklist' },
    notice: { icon: Bell, color: 'amber', label: 'Notice' },
    survey: { icon: ClipboardList, color: 'teal', label: 'Survey' },
    combined: { icon: Layers, color: 'indigo', label: 'Combined Task' },
    form_fields: { icon: List, color: 'orange', label: 'Form' },
    proof_upload: { icon: Upload, color: 'cyan', label: 'Upload' },
    signature: { icon: PenTool, color: 'pink', label: 'Signature' },
    policy_acknowledgment: { icon: CheckSquare, color: 'emerald', label: 'Policy' },
};

/**
 * Renders the full content of a section (quiz questions, checklist items, training content, etc.)
 */
const SectionContentRenderer = ({ section }) => {
    let content;
    try {
        content = typeof section.content === 'string' ? JSON.parse(section.content) : section.content;
    } catch {
        content = null;
    }

    if (!content) {
        return section.description ? (
            <p className="text-sm text-gray-600">{section.description}</p>
        ) : (
            <p className="text-sm text-gray-400 italic">No content available</p>
        );
    }

    const sectionType = section.section_type;

    // Quiz questions
    if (sectionType === 'quiz' && content.questions) {
        return (
            <div className="space-y-3">
                {content.passing_score && (
                    <p className="text-xs text-purple-600 font-medium">Passing score: {content.passing_score}%</p>
                )}
                {content.questions.map((q, qi) => (
                    <div key={qi} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <p className="text-sm font-medium text-gray-800 mb-2">
                            <span className="text-gray-400 mr-1.5">Q{qi + 1}.</span>
                            {q.question_text || q.question || q.text}
                        </p>
                        {q.options && (
                            <div className="space-y-1 ml-5">
                                {q.options.map((opt, oi) => {
                                    const isCorrect = q.correct_answer === oi || q.correct_answer === opt || q.correctAnswer === oi;
                                    return (
                                        <div key={oi} className={`flex items-center gap-2 text-xs py-1 px-2 rounded ${isCorrect ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-600'}`}>
                                            {isCorrect ? <Check className="w-3 h-3 text-green-500" /> : <Circle className="w-3 h-3 text-gray-300" />}
                                            {typeof opt === 'string' ? opt : opt.text || opt.label || JSON.stringify(opt)}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    }

    // Checklist items
    if (sectionType === 'checklist' && content.items) {
        return (
            <div className="space-y-2">
                {content.items.map((item, i) => (
                    <div key={i} className="flex items-start gap-2 py-1.5 px-3 bg-gray-50 rounded-lg border border-gray-100">
                        <CheckSquare className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{typeof item === 'string' ? item : item.text || item.label || JSON.stringify(item)}</span>
                    </div>
                ))}
            </div>
        );
    }

    // Training / reading content
    if (sectionType === 'training' || sectionType === 'policy_acknowledgment') {
        return (
            <div className="space-y-3">
                {content.content && (
                    <div className="prose prose-sm max-w-none text-gray-600 bg-gray-50 rounded-lg p-4 border border-gray-100 whitespace-pre-wrap">
                        {content.content}
                    </div>
                )}
                {content.body && (
                    <div className="prose prose-sm max-w-none text-gray-600 bg-gray-50 rounded-lg p-4 border border-gray-100 whitespace-pre-wrap">
                        {content.body}
                    </div>
                )}
                {content.policy_text && (
                    <div className="prose prose-sm max-w-none text-gray-600 bg-gray-50 rounded-lg p-4 border border-gray-100 whitespace-pre-wrap">
                        {content.policy_text}
                    </div>
                )}
                {content.acknowledgment_text && (
                    <p className="text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-2 rounded-lg border border-emerald-100">
                        ✓ {content.acknowledgment_text}
                    </p>
                )}
            </div>
        );
    }

    // Form fields
    if (sectionType === 'form_fields' && content.fields) {
        return (
            <div className="space-y-2">
                {content.fields.map((field, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 px-3 bg-gray-50 rounded-lg border border-gray-100">
                        <span className="text-xs font-medium text-gray-500 uppercase w-16 flex-shrink-0">{field.type || 'text'}</span>
                        <span className="text-sm text-gray-700">{field.label || field.name}</span>
                        {field.required && <span className="text-[10px] text-red-500 font-bold">REQUIRED</span>}
                    </div>
                ))}
            </div>
        );
    }

    // Proof upload
    if (sectionType === 'proof_upload') {
        return (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 text-center">
                <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Employee must upload a file as proof</p>
                {content.instructions && <p className="text-xs text-gray-400 mt-1">{content.instructions}</p>}
            </div>
        );
    }

    // Signature
    if (sectionType === 'signature') {
        return (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 text-center">
                <PenTool className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Employee must provide a digital signature</p>
            </div>
        );
    }

    // Fallback: show raw content
    return (
        <div className="prose prose-sm max-w-none text-gray-600 bg-gray-50 rounded-lg p-4 border border-gray-100 whitespace-pre-wrap">
            {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
        </div>
    );
};

/**
 * Renders the expanded content of a timeline item (task content, notice body, survey questions, etc.)
 */
const ItemExpandedContent = ({ item }) => {
    // Task with sections
    if (item.task?.sections && item.task.sections.length > 0) {
        return (
            <div className="space-y-3">
                {item.task.sections.map((section, si) => {
                    const sConfig = ITEM_TYPE_CONFIG[section.section_type] || ITEM_TYPE_CONFIG.task;
                    const SIcon = sConfig.icon;
                    return (
                        <div key={si} className="border border-gray-100 rounded-lg overflow-hidden">
                            <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                                <span className="w-5 h-5 rounded bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-500">{si + 1}</span>
                                <SIcon className="w-3.5 h-3.5 text-gray-400" />
                                <span className="text-sm font-medium text-gray-700 capitalize">{section.title || section.section_type?.replace('_', ' ')}</span>
                                <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-${sConfig.color}-50 text-${sConfig.color}-600 ml-auto`}>
                                    {sConfig.label}
                                </span>
                            </div>
                            <div className="p-4">
                                <SectionContentRenderer section={section} />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    // Task with direct content (quiz, checklist, training, etc.)
    if (item.task?.content) {
        return <SectionContentRenderer section={{ section_type: item.task.task_type || item.item_type || 'task', content: item.task.content, description: item.task.description }} />;
    }

    // Notice
    if (item.notice) {
        return (
            <div className="space-y-3">
                {item.notice.content && (
                    <div className="prose prose-sm max-w-none text-gray-600 bg-amber-50/50 rounded-lg p-4 border border-amber-100 whitespace-pre-wrap">
                        {item.notice.content}
                    </div>
                )}
                {item.notice.body && (
                    <div className="prose prose-sm max-w-none text-gray-600 bg-amber-50/50 rounded-lg p-4 border border-amber-100 whitespace-pre-wrap">
                        {item.notice.body}
                    </div>
                )}
                {!item.notice.content && !item.notice.body && item.notice.description && (
                    <p className="text-sm text-gray-600">{item.notice.description}</p>
                )}
            </div>
        );
    }

    // Survey
    if (item.survey?.questions) {
        return (
            <div className="space-y-3">
                {item.survey.description && (
                    <p className="text-sm text-gray-600 mb-2">{item.survey.description}</p>
                )}
                {item.survey.questions.map((q, qi) => {
                    const qType = q.question_type || q.type;
                    const qText = q.question_text || q.question || q.text;
                    // Parse options if stored as JSON string
                    let qOptions = q.options;
                    if (typeof qOptions === 'string') {
                        try { qOptions = JSON.parse(qOptions); } catch { qOptions = null; }
                    }
                    return (
                        <div key={qi} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                            <p className="text-sm font-medium text-gray-800 mb-2">
                                <span className="text-gray-400 mr-1.5">Q{qi + 1}.</span>
                                {qText}
                            </p>
                            {qType && <span className="text-[10px] text-teal-600 font-medium uppercase bg-teal-50 px-2 py-0.5 rounded">{qType}</span>}
                            {qOptions && Array.isArray(qOptions) && (
                                <div className="space-y-1 ml-5 mt-2">
                                    {qOptions.map((opt, oi) => (
                                        <div key={oi} className="flex items-center gap-2 text-xs text-gray-600 py-1">
                                            <Circle className="w-3 h-3 text-gray-300" />
                                            {typeof opt === 'string' ? opt : opt.text || opt.label || JSON.stringify(opt)}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    return <p className="text-sm text-gray-400 italic">No detailed content available for this item.</p>;
};


/**
 * AdminCoursePreviewModal
 * Shows a read-only preview of a course with its timeline items,
 * with expand/collapse functionality to view full content.
 */
const AdminCoursePreviewModal = ({ isOpen, onClose, courseData, timelineItems = [] }) => {
    const [expandedItems, setExpandedItems] = useState({});

    if (!isOpen || !courseData) return null;

    const toggleItem = (itemKey) => {
        setExpandedItems(prev => ({ ...prev, [itemKey]: !prev[itemKey] }));
    };

    // Group timeline items by day
    const itemsByDay = {};
    const sortedItems = [...timelineItems].sort((a, b) => (a.trigger_day || 0) - (b.trigger_day || 0));
    sortedItems.forEach(item => {
        const day = item.trigger_day || item.relative_day || 0;
        if (!itemsByDay[day]) itemsByDay[day] = [];
        itemsByDay[day].push(item);
    });
    const days = Object.keys(itemsByDay).map(Number).sort((a, b) => a - b);

    const getItemConfig = (item) => {
        const type = item.item_type || item.task?.task_type || 'task';
        return ITEM_TYPE_CONFIG[type] || ITEM_TYPE_CONFIG.task;
    };

    const getItemTitle = (item) => {
        return item.title || item.name || item.task?.title || item.notice?.title || item.survey?.title || 'Untitled';
    };

    const getItemDescription = (item) => {
        return item.description || item.task?.description || item.notice?.description || item.survey?.description || '';
    };

    return (
        <div className="fixed inset-0 z-[60] flex flex-col bg-gray-50">
            {/* Header Bar */}
            <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0 shadow-sm">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="flex items-center gap-2">
                        <Eye className="w-5 h-5 text-indigo-600" />
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">
                            <AlertCircle className="w-3 h-3" />
                            Admin Preview
                        </span>
                    </div>
                    <span className="text-sm text-gray-400">|</span>
                    <h1 className="text-base font-semibold text-gray-900 truncate">
                        {courseData.name || 'Course Preview'}
                    </h1>
                </div>
                <button
                    onClick={onClose}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                    <X className="w-4 h-4" />
                    Close Preview
                </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto">
                <div className="max-w-4xl mx-auto px-6 py-8">
                    {/* Read-only notice */}
                    <div className="mb-6 flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-3">
                        <Eye className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                        <p className="text-sm text-indigo-700">
                            This is a <strong>read-only preview</strong> showing how the course will appear to employees. Click on items to expand their content.
                        </p>
                    </div>

                    {/* Course Header */}
                    <div className="bg-white shadow-sm rounded-xl p-8 mb-6 border border-gray-100">
                        <div className="flex items-start gap-4 mb-4">
                            <div className="p-3 bg-violet-100 rounded-xl text-violet-600">
                                <BookOpen className="w-7 h-7" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                    {courseData.name || 'Untitled Course'}
                                </h2>
                                {courseData.description && (
                                    <p className="text-gray-600 whitespace-pre-wrap">{courseData.description}</p>
                                )}
                            </div>
                        </div>

                        {/* Course Metadata */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 pt-4 border-t border-gray-100">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-violet-50 text-violet-700 rounded-full text-xs font-medium capitalize">
                                {courseData.courseType || 'onboarding'} Course
                            </span>
                            {courseData.duration_days && (
                                <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                                    <Clock className="w-3.5 h-3.5" />
                                    {courseData.duration_days} days
                                </span>
                            )}
                            {courseData.enrollment_method && (
                                <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                                    <Users className="w-3.5 h-3.5" />
                                    {courseData.enrollment_method === 'automatic' ? 'Auto-enrollment' : 'Manual enrollment'}
                                </span>
                            )}
                            {timelineItems.length > 0 && (
                                <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                                    <Calendar className="w-3.5 h-3.5" />
                                    {timelineItems.length} timeline {timelineItems.length === 1 ? 'item' : 'items'}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Timeline */}
                    {sortedItems.length > 0 ? (
                        <div className="space-y-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-violet-600" />
                                Course Timeline
                            </h3>

                            {days.map((day, dayIndex) => (
                                <div key={day} className="relative">
                                    {/* Day Header */}
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-10 h-10 rounded-full bg-violet-600 text-white flex items-center justify-center text-sm font-bold shadow-md">
                                            D{day}
                                        </div>
                                        <div>
                                            <span className="text-sm font-semibold text-gray-900">Day {day}</span>
                                            <span className="text-xs text-gray-400 ml-2">
                                                {itemsByDay[day].length} {itemsByDay[day].length === 1 ? 'activity' : 'activities'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Items for this day */}
                                    <div className={`ml-5 pl-8 space-y-3 pb-6 ${dayIndex < days.length - 1 ? 'border-l-2 border-violet-100' : ''}`}>
                                        {itemsByDay[day].map((item, itemIndex) => {
                                            const config = getItemConfig(item);
                                            const Icon = config.icon;
                                            const title = getItemTitle(item);
                                            const description = getItemDescription(item);
                                            const itemKey = `${day}-${itemIndex}`;
                                            const isExpanded = !!expandedItems[itemKey];

                                            return (
                                                <div
                                                    key={item.id || itemKey}
                                                    className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-all shadow-sm"
                                                >
                                                    {/* Clickable Header */}
                                                    <button
                                                        onClick={() => toggleItem(itemKey)}
                                                        className={`w-full px-5 py-4 flex items-start gap-3 text-left transition-colors hover:bg-gray-50/50 ${isExpanded ? 'border-b border-gray-100' : ''}`}
                                                    >
                                                        <div className={`p-2 rounded-lg bg-${config.color}-50 text-${config.color}-600 flex-shrink-0 mt-0.5`}>
                                                            <Icon className="w-5 h-5" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <h4 className="font-semibold text-gray-900 truncate">{title}</h4>
                                                                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-${config.color}-50 text-${config.color}-700`}>
                                                                    {config.label}
                                                                </span>
                                                            </div>
                                                            {description && (
                                                                <p className={`text-sm text-gray-500 ${isExpanded ? '' : 'line-clamp-2'}`}>{description}</p>
                                                            )}
                                                        </div>
                                                        <div className="flex-shrink-0 mt-1">
                                                            {isExpanded
                                                                ? <ChevronDown className="w-5 h-5 text-gray-400" />
                                                                : <ChevronRight className="w-5 h-5 text-gray-400" />
                                                            }
                                                        </div>
                                                    </button>

                                                    {/* Expanded Content */}
                                                    {isExpanded && (
                                                        <div className="px-5 py-4 bg-gray-50/30">
                                                            <ItemExpandedContent item={item} />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="bg-white shadow-sm rounded-xl p-12 border border-gray-100 text-center">
                            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-500 mb-2">No Timeline Items Yet</h3>
                            <p className="text-sm text-gray-400">
                                Use the AI chat to generate course content and timeline items.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminCoursePreviewModal;
